import React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import { useNavigate } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import { activityApi } from "../../api/activity";
import { erpApi } from "../../api/erp";
import { getProjectBillingAccess } from "../../utils/projectBillingAccess";
import { projectBusinessGoals } from "../../utils/projectCapabilities";
import { getProjectPublicUrl } from "../../utils/projectDeployment";
import { buildProjectActionTasks } from "../../utils/projectActionTasks";
import { normalizeProjectModules } from "../../utils/projectModules";
import { hasProjectPermission, normalizeProjectAccess } from "../../utils/projectAccess";
import { activityDisplayDescription, activityDisplayTitle } from "../../utils/activityPresentation";
import DashboardLoadingState from "./DashboardLoadingState";

const BRAND = {
  orange: "#F35A3A",
  dark: "var(--wp-panel-text-primary, #19242F)",
  purple: "#6957E8",
  green: "#26A269",
  blue: "#2878D4",
  yellow: "#FFC857",
};

const COLOR_MAP = {
  orange: { main: BRAND.orange, soft: "rgba(243,90,58,.11)", border: "rgba(243,90,58,.24)" },
  purple: { main: BRAND.purple, soft: "rgba(105,87,232,.10)", border: "rgba(105,87,232,.22)" },
  green: { main: BRAND.green, soft: "rgba(38,162,105,.10)", border: "rgba(38,162,105,.22)" },
  blue: { main: BRAND.blue, soft: "rgba(40,120,212,.10)", border: "rgba(40,120,212,.22)" },
  yellow: { main: "#9A6500", soft: "rgba(255,200,87,.18)", border: "rgba(154,101,0,.2)" },
  dark: { main: BRAND.dark, soft: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", border: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.16)" },
};

function ActionTile({
  icon,
  title,
  description,
  label = "Abrir",
  color = "orange",
  disabled = false,
  badge,
  featured = false,
  onClick,
}) {
  const palette = COLOR_MAP[color] || COLOR_MAP.orange;

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`${title}: ${disabled ? "ver acceso" : label}`}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: { xs: 48, sm: 0 },
        display: "block",
        textAlign: "left",
        borderRadius: { xs: 2.5, sm: 3.4 },
        overflow: "hidden",
        touchAction: "manipulation",
        "&:focus-visible": { boxShadow: `0 0 0 4px ${palette.soft}` },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          minHeight: featured ? { xs: 82, sm: 168 } : { xs: 72, sm: 142 },
          borderRadius: { xs: 2.5, sm: 3.4 },
          border: `1px solid ${disabled ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.1)" : palette.border}`,
          background: featured && !disabled
            ? `linear-gradient(145deg, #fff 0%, ${palette.soft} 100%)`
            : disabled
              ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)"
              : "#fff",
          p: featured ? { xs: 1.1, sm: 2.2 } : { xs: 1, sm: 1.75 },
          opacity: disabled ? .78 : 1,
          display: "grid",
          gridTemplateColumns: { xs: "auto minmax(0, 1fr) auto", sm: "auto minmax(0, 1fr)" },
          gridTemplateRows: { xs: "1fr", sm: "auto 1fr" },
          gridTemplateAreas: { xs: "'icon content trail'", sm: "'icon trail' 'content content'" },
          alignItems: { xs: "center", sm: "start" },
          columnGap: { xs: 1, sm: 1.15 },
          rowGap: { xs: 0, sm: 1.1 },
          transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
          "&:hover": {
            transform: { xs: "none", sm: "translateY(-3px)" },
            boxShadow: "0 15px 34px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.1)",
            borderColor: disabled ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.2)" : palette.main,
          },
        }}
      >
        <Box
          sx={{
            gridArea: "icon",
            width: featured ? { xs: 40, sm: 48 } : { xs: 38, sm: 43 },
            height: featured ? { xs: 40, sm: 48 } : { xs: 38, sm: 43 },
            borderRadius: { xs: 2.1, sm: 2.7 },
            display: "grid",
            placeItems: "center",
            color: disabled ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.5)" : palette.main,
            bgcolor: disabled ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)" : palette.soft,
            flexShrink: 0,
            "& svg": { fontSize: featured ? { xs: 22, sm: 26 } : { xs: 21, sm: 23 } },
          }}
        >
          {disabled ? <LockClockRoundedIcon /> : icon}
        </Box>

        <Box sx={{ gridArea: "trail", justifySelf: "end", alignSelf: { xs: "center", sm: "start" }, pl: .35 }}>
          {badge ? (
            <Chip
              size="small"
              label={badge}
              sx={{
                height: 22,
                maxWidth: { xs: 96, sm: 150 },
                fontSize: ".61rem",
                fontWeight: 950,
                bgcolor: disabled ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)" : palette.soft,
                color: disabled ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.58)" : palette.main,
              }}
            />
          ) : (
            <ArrowForwardRoundedIcon sx={{ color: disabled ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.35)" : palette.main, fontSize: { xs: 20, sm: 21 } }} />
          )}
        </Box>

        <Box sx={{ gridArea: "content", minWidth: 0, alignSelf: "center" }}>
          <Typography
            sx={{
              color: BRAND.dark,
              fontSize: featured ? { xs: ".9rem", sm: "1.08rem" } : { xs: ".82rem", sm: ".96rem" },
              lineHeight: 1.16,
              fontWeight: 1000,
              letterSpacing: "-.15px",
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              display: { xs: featured ? "-webkit-box" : "none", sm: "block" },
              mt: { xs: .25, sm: .48 },
              color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
              fontSize: featured ? { xs: ".68rem", sm: ".8rem" } : ".75rem",
              lineHeight: 1.34,
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: { xs: 1, sm: "unset" },
              overflow: { xs: "hidden", sm: "visible" },
            }}
          >
            {description}
          </Typography>
          <Typography
            sx={{
              mt: { xs: .28, sm: .75 },
              color: disabled ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.52)" : palette.main,
              fontSize: { xs: ".65rem", sm: ".72rem" },
              fontWeight: 1000,
            }}
          >
            {disabled ? "Ver acceso" : label}
          </Typography>
        </Box>
      </Paper>
    </ButtonBase>
  );
}

const OPERATIONAL_ROUTE_SEGMENTS = ["/sistema", "/clientes", "/productos", "/agenda", "/pedidos", "/mesas"];

function isOperationalDestination(path = "") {
  return OPERATIONAL_ROUTE_SEGMENTS.some((segment) => String(path).includes(segment));
}

function projectIdentifier(project) {
  return project?.slug || project?.id || project?._id || "";
}

function relativeTime(value) {
  const timestamp = new Date(value || 0).getTime();
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "";
  const minutes = Math.max(0, Math.round((Date.now() - timestamp) / 60000));
  if (minutes < 1) return "Ahora";
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} d`;
}

export default function ClientCommandCenter({
  projects,
  activeProject,
  setActiveProject,
  projectCreation,
  loading,
  error,
}) {
  const navigate = useNavigate();
  const [readiness, setReadiness] = React.useState(null);
  const [activitySummary, setActivitySummary] = React.useState(null);
  const [recentActivity, setRecentActivity] = React.useState([]);
  const [activityLoading, setActivityLoading] = React.useState(false);
  const [erpBootstrap, setErpBootstrap] = React.useState(null);
  const [erpBootstrapLoading, setErpBootstrapLoading] = React.useState(false);
  const modules = normalizeProjectModules(activeProject?.modules);
  const memberAccess = normalizeProjectAccess(activeProject?.memberAccess);
  const billingAccess = getProjectBillingAccess(activeProject);
  const projectKey = projectIdentifier(activeProject);
  const encodedProjectKey = encodeURIComponent(projectKey);
  const projectBase = projectKey
    ? `/dashboard/proyectos/${encodedProjectKey}`
    : "/dashboard/proyectos";
  const publicUrl = getProjectPublicUrl(activeProject);
  const operationallyLocked = billingAccess.isLandingOnly || billingAccess.isSuspended;
  const itemPath = modules.system ? `${projectBase}/sistema/items` : `${projectBase}/productos`;
  const subscribePath = `${projectBase}/configuracion/suscripcion`;
  const businessGoals = projectBusinessGoals(activeProject);

  React.useEffect(() => {
    let cancelled = false;
    const fallback = activeProject?.readiness || null;
    setReadiness(fallback);

    if (!projectKey) {
      return () => { cancelled = true; };
    }

    projectsApi.getReadiness(projectKey)
      .then((result) => {
        if (!cancelled) setReadiness(result?.readiness || fallback);
      })
      .catch(() => {
        if (!cancelled) setReadiness(fallback);
      });

    return () => { cancelled = true; };
  }, [projectKey, activeProject?.readiness?.computedAt]);

  React.useEffect(() => {
    let cancelled = false;
    setActivitySummary(null);
    setRecentActivity([]);

    if (!projectKey) {
      setActivityLoading(false);
      return () => { cancelled = true; };
    }

    setActivityLoading(true);
    Promise.allSettled([
      activityApi.projectSummary(projectKey, 168),
      activityApi.project(projectKey, { page: 1, pageSize: 5, presentation: "human" }),
    ]).then(([summaryResult, activityResult]) => {
      if (cancelled) return;
      setActivitySummary(summaryResult.status === "fulfilled" ? summaryResult.value?.summary || null : null);
      setRecentActivity(
        activityResult.status === "fulfilled" && Array.isArray(activityResult.value?.items)
          ? activityResult.value.items
          : [],
      );
    }).finally(() => {
      if (!cancelled) setActivityLoading(false);
    });

    return () => { cancelled = true; };
  }, [projectKey]);

  React.useEffect(() => {
    let cancelled = false;
    setErpBootstrap(null);
    if (!projectKey || !modules.system) {
      setErpBootstrapLoading(false);
      return () => { cancelled = true; };
    }

    setErpBootstrapLoading(true);
    erpApi.bootstrap(projectKey)
      .then((result) => {
        if (!cancelled) setErpBootstrap(result || null);
      })
      .catch(() => {
        if (!cancelled) setErpBootstrap(null);
      })
      .finally(() => {
        if (!cancelled) setErpBootstrapLoading(false);
      });

    return () => { cancelled = true; };
  }, [modules.system, projectKey]);

  const go = React.useCallback((path, { operational = false } = {}) => {
    if (!projectKey) {
      navigate("/dashboard/proyectos");
      return;
    }
    if (operational && operationallyLocked) {
      navigate(subscribePath);
      return;
    }
    navigate(path);
  }, [navigate, operationallyLocked, projectKey, subscribePath]);

  const openPublicSite = () => {
    if (publicUrl) {
      window.open(publicUrl, "_blank", "noopener,noreferrer");
      return;
    }
    go(`${projectBase}/pagina`);
  };

  if (loading && !activeProject) {
    return <DashboardLoadingState label="Preparando tu panel…" minHeight={360} />;
  }

  if (!activeProject) {
    return (
      <Stack spacing={{ xs: 1.5, sm: 2.25 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            p: { xs: 2, sm: 3.25 },
            border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
            background: "linear-gradient(135deg, #fff 0%, rgba(243,90,58,.035) 100%)",
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: BRAND.dark, fontWeight: 1000, letterSpacing: { xs: "-.35px", sm: "-.6px" } }}
          >
            ¿Cómo querés empezar?
          </Typography>
          <Typography sx={{ mt: .7, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", maxWidth: 760 }}>
            Tu cuenta ya está lista. Creá tu negocio o abrí tu Red de referidos; el enlace personal no requiere un proyecto.
          </Typography>

          <Grid container spacing={{ xs: 1.25, sm: 1.75 }} sx={{ mt: { xs: .6, sm: 1 } }}>
            <Grid item xs={12} md={6}>
              <ActionTile
                featured
                icon={<StorefrontRoundedIcon />}
                title="Crear mi negocio"
                description="Empezá tu primer proyecto con el nombre del negocio y completá el resto después."
                label={projectCreation?.canCreate === false ? "Ver proyectos" : "Crear negocio"}
                color="orange"
                onClick={() => navigate("/dashboard/proyectos")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ActionTile
                featured
                icon={<ShareRoundedIcon />}
                title="Trabajar con WebProfit"
                description="Abrí tu Red de referidos, copiá tu enlace personal y empezá a sumar clientes sin crear un proyecto propio."
                label="Abrir Red de referidos"
                color="purple"
                onClick={() => navigate("/dashboard/referidos")}
              />
            </Grid>
          </Grid>
        </Paper>
      </Stack>
    );
  }


  const serviceFirst =
    businessGoals.includes("offerServices") ||
    (["services", "gym"].includes(activeProject?.businessProfile) && !businessGoals.includes("sellProducts"));
  const preferredItemKind = serviceFirst ? "service" : "product";
  const permissionPending = !memberAccess.resolved;
  const canCreateItem = (modules.products || modules.system)
    && (permissionPending || (
      hasProjectPermission(memberAccess, "items.read")
      && hasProjectPermission(memberAccess, "items.create")
    ));


  const readinessAvailable = Number.isFinite(Number(readiness?.requiredCount));
  const operationalTasks = readinessAvailable
    ? buildProjectActionTasks({ project: activeProject, readiness, modules }).slice(0, 3)
    : [];
  const activityMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.total || 0);
  const visitorMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.uniqueVisitors || 0);
  const intentMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.highIntent || 0);
  const teamMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.uniquePanelUsers || 0);


  const readinessFacts = readiness?.facts && typeof readiness.facts === "object" ? readiness.facts : {};
  const boundedReadinessScore = readinessAvailable
    ? Math.max(0, Math.min(100, Number.isFinite(Number(readiness?.score))
      ? Number(readiness.score)
      : Math.round((Number(readiness?.completedCount || 0) / Math.max(1, Number(readiness?.requiredCount || 1))) * 100)))
    : null;
  const factFlag = (fact) => {
    if (!readinessAvailable || !fact || typeof fact !== "object") return null;
    if (fact.complete === true) return true;
    if (fact.required === true) return false;
    return null;
  };
  const publicationReady = factFlag(readinessFacts?.publication);
  const pageStartedFlag = factFlag(readinessFacts?.pageStarted);
  const pageReady = Boolean(publicUrl) || publicationReady === true;
  const pageStarted = pageReady ? true : pageStartedFlag;
  const firstItemReady = factFlag(readinessFacts?.firstItem);
  const contactReady = factFlag(readinessFacts?.contact);
  const managementReady = factFlag(readinessFacts?.operationalManagement);
  const nextTask = operationalTasks[0] || null;
  const itemLabel = serviceFirst ? "servicio" : "producto";

  const erpProgress = erpBootstrap?.progress || {};
  const implementationProgress = erpBootstrap?.implementationProgress || {};
  const erpItemCount = Number.isFinite(Number(erpProgress.itemCount)) ? Number(erpProgress.itemCount) : null;
  const stockTrackedItems = Number.isFinite(Number(implementationProgress.stockTrackedItems))
    ? Number(implementationProgress.stockTrackedItems)
    : null;
  const stockPendingItems = Number.isFinite(Number(implementationProgress.stockPendingItems))
    ? Number(implementationProgress.stockPendingItems)
    : null;
  const activeStockCount = implementationProgress.activeStockCount || null;
  const lastCompletedStockCount = implementationProgress.lastCompletedStockCount || null;
  const stockMigrationReconciliation = lastCompletedStockCount?.migrationReconciliation || null;
  const historicalMovementCount = Number(erpProgress.historicalMovementCount || 0);
  const liveConfirmedMovementCount = Number.isFinite(Number(erpProgress.liveConfirmedMovementCount))
    ? Number(erpProgress.liveConfirmedMovementCount)
    : Math.max(0, Number(erpProgress.confirmedMovementCount || 0) - historicalMovementCount);
  const liveActivityMovementCount = Number.isFinite(Number(erpProgress.liveActivityMovementCount))
    ? Number(erpProgress.liveActivityMovementCount)
    : liveConfirmedMovementCount;
  const cashRegisterCount = Array.isArray(erpBootstrap?.registers) ? erpBootstrap.registers.length : 0;
  const catalogReady = erpItemCount == null ? firstItemReady === true : erpItemCount > 0;
  const inventoryApplies = stockTrackedItems == null ? !serviceFirst : stockTrackedItems > 0;
  const stockCountCompleted = Boolean(lastCompletedStockCount?.completedAt || lastCompletedStockCount?.status === "completed");
  const stockReady = !inventoryApplies ? catalogReady : stockCountCompleted;
  const cashReady = cashRegisterCount > 0;
  const operationStarted = liveActivityMovementCount > 0;

  const erpSetupSteps = modules.system ? [
    {
      key: "catalog",
      label: serviceFirst ? "Cargá tus servicios" : "Cargá todos tus productos",
      detail: catalogReady
        ? erpItemCount != null
          ? `${erpItemCount} ítem${erpItemCount === 1 ? "" : "s"} cargado${erpItemCount === 1 ? "" : "s"}. Antes de contar, revisá que no falte ninguno.`
          : "El catálogo ya tiene contenido. Antes de contar, revisá que no falte ningún ítem."
        : "Primero armá el catálogo. El conteo inicial usa estos ítems como base.",
      complete: catalogReady,
      actionLabel: catalogReady ? `Cargar otro ${itemLabel}` : `Cargar ${itemLabel}s`,
      onClick: canCreateItem ? () => go(itemPath, { operational: true }) : undefined,
    },
    ...(inventoryApplies ? [{
      key: "stock",
      label: "Contá el stock físico inicial",
      detail: activeStockCount
        ? `Conteo en curso: ${Number(activeStockCount.countedLines || 0)} de ${Number(activeStockCount.totalLines || 0)} líneas contadas.`
        : stockReady
          ? stockMigrationReconciliation?.available
            ? `Conteo físico cerrado y conciliado con ${Number(stockMigrationReconciliation.historicalDocuments || 0)} documento${Number(stockMigrationReconciliation.historicalDocuments || 0) === 1 ? "" : "s"} histórico${Number(stockMigrationReconciliation.historicalDocuments || 0) === 1 ? "" : "s"}. El stock contado quedó como punto de partida${Number(stockMigrationReconciliation.linesWithUnexplainedOutflow || 0) > 0 ? `; ${Number(stockMigrationReconciliation.linesWithUnexplainedOutflow || 0)} existencia${Number(stockMigrationReconciliation.linesWithUnexplainedOutflow || 0) === 1 ? " tiene" : "s tienen"} diferencias históricas estimadas` : ""}.`
            : "Conteo físico inicial completado. Desde acá, WebProfit puede mantener el stock al día."
          : stockPendingItems === 0 && stockTrackedItems > 0
            ? "El stock tiene valores cargados, pero todavía falta confirmar el conteo físico inicial."
            : "Contá lo que realmente hay hoy. No necesitás reconstruir cada venta vieja para empezar bien.",
      complete: stockReady,
      locked: !catalogReady,
      actionLabel: activeStockCount ? "Continuar conteo" : stockReady ? "Revisar stock" : "Hacer conteo",
      onClick: () => go(`${projectBase}/sistema/stock?conteo=1`, { operational: true }),
    }] : []),
    {
      key: "cash",
      label: "Creá tus cajas y cuentas",
      detail: cashReady
        ? `${cashRegisterCount} caja${cashRegisterCount === 1 ? "" : "s"} o cuenta${cashRegisterCount === 1 ? "" : "s"} configurada${cashRegisterCount === 1 ? "" : "s"}.`
        : "Hacelo después del conteo: efectivo, bancos, billeteras y tarjetas arrancan desde su saldo real.",
      complete: cashReady,
      locked: inventoryApplies ? !stockReady : !catalogReady,
      actionLabel: cashReady ? "Ver cajas" : "Crear cajas",
      onClick: () => go(`${projectBase}/sistema/caja`, { operational: true }),
    },
    {
      key: "operate",
      label: "Empezá a registrar desde hoy",
      detail: operationStarted
        ? `${liveActivityMovementCount} movimiento${liveActivityMovementCount === 1 ? "" : "s"} operativo${liveActivityMovementCount === 1 ? "" : "s"} confirmado${liveActivityMovementCount === 1 ? "" : "s"}.`
        : "Desde este punto, registrá ventas, compras y gastos nuevos para que stock y números sigan solos.",
      complete: operationStarted,
      locked: !cashReady,
      actionLabel: operationStarted ? "Ver movimientos" : "Registrar primero",
      onClick: () => go(`${projectBase}/sistema/movimientos`, { operational: true }),
    },
  ] : [];
  const completedErpSteps = erpSetupSteps.filter((step) => step.complete).length;
  const nextErpStepIndex = erpSetupSteps.findIndex((step) => !step.complete);
  const erpSetupComplete = Boolean(erpSetupSteps.length && completedErpSteps === erpSetupSteps.length);

  const statusRows = [
    {
      key: "web",
      label: "Web",
      value: pageReady ? "Publicada" : pageStarted === true ? "En preparación" : pageStarted === false ? "Por preparar" : "Disponible",
      ready: pageReady,
      pending: pageStarted === false,
      icon: <PublicRoundedIcon sx={{ fontSize: 18 }} />,
      onClick: openPublicSite,
    },
    {
      key: "catalog",
      label: serviceFirst ? "Servicios" : "Productos",
      value: firstItemReady === true ? "Con contenido" : firstItemReady === false ? `Falta el primer ${itemLabel}` : "Disponible",
      ready: firstItemReady === true,
      pending: firstItemReady === false,
      icon: serviceFirst ? <AddBusinessRoundedIcon sx={{ fontSize: 18 }} /> : <Inventory2RoundedIcon sx={{ fontSize: 18 }} />,
      onClick: canCreateItem ? () => go(itemPath, { operational: true }) : undefined,
    },
    {
      key: "contact",
      label: "Contacto",
      value: contactReady === true ? "Completo" : contactReady === false ? "Falta revisar" : "Disponible",
      ready: contactReady === true,
      pending: contactReady === false,
      icon: <GroupRoundedIcon sx={{ fontSize: 18 }} />,
      onClick: modules.landing ? () => go(`${projectBase}/pagina?mision=datos`) : undefined,
    },
    ...(modules.system ? [{
      key: "management",
      label: "Gestión",
      value: managementReady === true ? "En marcha" : managementReady === false ? "Por iniciar" : "Disponible",
      ready: managementReady === true,
      pending: managementReady === false,
      icon: <WarehouseRoundedIcon sx={{ fontSize: 18 }} />,
      onClick: () => go(`${projectBase}/sistema`, { operational: true }),
    }] : []),
  ].slice(0, 4);

  const keepBuildingAction = canCreateItem ? {
    title: `Cargar otro ${itemLabel}`,
    description: firstItemReady === true
      ? `Tu catálogo ya empezó. Sumá otro ${itemLabel} cuando quieras.`
      : firstItemReady === false
        ? `Empezá el catálogo con tu primer ${itemLabel}.`
        : `Entrá al catálogo para revisar o sumar ${itemLabel}s.`,
    label: firstItemReady === true ? `Nuevo ${itemLabel}` : firstItemReady === false ? `Crear ${itemLabel}` : "Abrir catálogo",
    onClick: () => go(`${itemPath}?crear=1&tipo=${preferredItemKind}`, { operational: true }),
    icon: serviceFirst ? <AddBusinessRoundedIcon /> : <Inventory2RoundedIcon />,
  } : null;

  const focusAction = nextTask ? {
    title: nextTask.label,
    description: nextTask.description,
    label: "Resolver ahora",
    onClick: () => go(nextTask.path, { operational: isOperationalDestination(nextTask.path) }),
    icon: <TaskAltRoundedIcon />,
  } : keepBuildingAction || {
    title: pageReady ? "Revisá tu web como la ve un cliente" : "Terminá de preparar tu web",
    description: pageReady
      ? "La puesta a punto principal está completa. Entrá a verla y seguí ajustando lo que necesites."
      : "Abrí la página y completá lo que falte antes de compartirla.",
    label: pageReady ? "Ver mi web" : "Preparar web",
    onClick: openPublicSite,
    icon: <PublicRoundedIcon />,
  };

  return (
    <Stack spacing={{ xs: .8, sm: .9 }} sx={{ minWidth: 0, pb: .5 }}>
      {error ? <Alert severity="error" sx={{ borderRadius: 2.2, py: 0 }}>{error}</Alert> : null}

      {operationallyLocked ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => navigate(subscribePath)} sx={{ fontWeight: 950, textTransform: "none" }}>
              Reactivar
            </Button>
          }
          sx={{ borderRadius: 2.2, py: 0, "& .MuiAlert-message": { py: .55 } }}
        >
          Las funciones operativas están pausadas; tu web sigue disponible.
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          p: { xs: .8, sm: .9 },
          borderRadius: 2.5,
          border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
          bgcolor: "var(--wp-panel-surface, #fff)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: .7, sm: 1 }} alignItems={{ sm: "center" }}>
          <Stack direction="row" spacing={.8} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(105,87,232,.08)", color: BRAND.purple, flexShrink: 0 }}>
              <StorefrontRoundedIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap sx={{ color: BRAND.dark, fontWeight: 1000, fontSize: { xs: ".9rem", sm: ".98rem" }, lineHeight: 1.15 }}>
                {activeProject?.name || "Tu negocio"}
              </Typography>
              <Stack direction="row" spacing={.7} alignItems="center" sx={{ mt: .28, minWidth: 0 }}>
                <Typography noWrap sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.58)", fontSize: ".65rem", fontWeight: 850 }}>
                  {boundedReadinessScore == null ? "Estado del negocio" : `Puesta a punto ${Math.round(boundedReadinessScore)}%`}
                </Typography>
                {boundedReadinessScore != null ? (
                  <Box sx={{ width: { xs: 70, sm: 92 }, height: 4, borderRadius: 99, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", overflow: "hidden", flexShrink: 0 }}>
                    <Box sx={{ width: `${boundedReadinessScore}%`, height: "100%", bgcolor: boundedReadinessScore >= 100 ? BRAND.green : BRAND.orange, borderRadius: 99 }} />
                  </Box>
                ) : null}
              </Stack>
            </Box>
          </Stack>

          {projects.length > 1 ? (
            <Select
              size="small"
              value={projectIdentifier(activeProject)}
              onChange={(event) => setActiveProject(event.target.value)}
              sx={{
                minWidth: { xs: "100%", sm: 180 },
                maxWidth: { sm: 230 },
                minHeight: 36,
                borderRadius: 2,
                fontSize: ".73rem",
                fontWeight: 900,
                bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)",
                ".MuiSelect-select": { py: .65 },
              }}
            >
              {projects.map((project) => (
                <MenuItem key={project.id || project._id || project.slug} value={projectIdentifier(project)}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          ) : null}

          <Button
            variant="contained"
            startIcon={<PublicRoundedIcon />}
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={openPublicSite}
            sx={{
              minHeight: 38,
              px: { xs: 1.4, sm: 1.8 },
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 1000,
              bgcolor: BRAND.orange,
              boxShadow: "0 8px 20px rgba(243,90,58,.20)",
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: BRAND.orange, filter: "brightness(.94)" },
            }}
          >
            Ver mi web
          </Button>
        </Stack>
      </Paper>

      <Grid container spacing={{ xs: .8, sm: .9 }}>
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              minHeight: { xs: 0, md: 244 },
              p: { xs: 1.05, sm: 1.25 },
              borderRadius: 2.5,
              border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
              bgcolor: "var(--wp-panel-surface, #fff)",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: .7 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: BRAND.dark, fontSize: ".9rem", fontWeight: 1000 }}>Puesta a punto del negocio</Typography>
                <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.58)", fontSize: ".65rem", lineHeight: 1.3 }}>
                  {erpBootstrapLoading ? "Revisando tu sistema…" : erpSetupComplete ? "La base operativa está lista." : "Seguí este orden para arrancar con una verdad limpia."}
                </Typography>
              </Box>
              {erpSetupSteps.length ? (
                <Chip
                  size="small"
                  label={`${completedErpSteps}/${erpSetupSteps.length}`}
                  sx={{ height: 22, fontSize: ".62rem", fontWeight: 1000, bgcolor: erpSetupComplete ? "rgba(38,162,105,.09)" : "rgba(243,90,58,.08)", color: erpSetupComplete ? BRAND.green : BRAND.orange }}
                />
              ) : null}
            </Stack>

            {modules.system ? (
              <Stack spacing={.35}>
                {erpSetupSteps.map((step, index) => {
                  const active = nextErpStepIndex === index;
                  const locked = step.locked && !step.complete;
                  return (
                    <ButtonBase
                      key={step.key}
                      disabled={locked || !step.onClick}
                      onClick={step.onClick}
                      sx={{ width: "100%", textAlign: "left", borderRadius: 1.8 }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          minHeight: 42,
                          px: .7,
                          py: .5,
                          borderRadius: 1.8,
                          display: "grid",
                          gridTemplateColumns: "28px minmax(0,1fr) auto",
                          gap: .7,
                          alignItems: "center",
                          bgcolor: active ? "rgba(243,90,58,.045)" : "transparent",
                          border: active ? "1px solid rgba(243,90,58,.12)" : "1px solid transparent",
                          opacity: locked ? .48 : 1,
                          "&:hover": { bgcolor: locked ? "transparent" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)" },
                        }}
                      >
                        <Box sx={{ width: 26, height: 26, borderRadius: 99, display: "grid", placeItems: "center", bgcolor: step.complete ? "rgba(38,162,105,.10)" : active ? "rgba(243,90,58,.11)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)", color: step.complete ? BRAND.green : active ? BRAND.orange : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.5)", fontSize: ".67rem", fontWeight: 1000 }}>
                          {step.complete ? <TaskAltRoundedIcon sx={{ fontSize: 16 }} /> : index + 1}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ color: BRAND.dark, fontSize: ".73rem", fontWeight: 1000, lineHeight: 1.15 }}>{step.label}</Typography>
                          <Typography noWrap sx={{ mt: .15, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.58)", fontSize: ".62rem" }}>{step.detail}</Typography>
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={.15} sx={{ color: step.complete ? BRAND.green : active ? BRAND.orange : BRAND.dark, flexShrink: 0 }}>
                          <Typography sx={{ display: { xs: "none", sm: "block" }, fontSize: ".61rem", fontWeight: 950 }}>{locked ? "Primero el paso anterior" : step.actionLabel}</Typography>
                          {!locked ? <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} /> : null}
                        </Stack>
                      </Box>
                    </ButtonBase>
                  );
                })}

                {!erpSetupComplete ? (
                  <Box sx={{ mt: .35, px: .75, py: .55, borderRadius: 1.7, bgcolor: "rgba(105,87,232,.045)", border: "1px solid rgba(105,87,232,.10)" }}>
                    <Typography sx={{ color: BRAND.dark, fontSize: ".63rem", fontWeight: 900, lineHeight: 1.3 }}>
                      Si no tenés toda la historia, no la reconstruyas antes de arrancar: completá el catálogo, contá el stock real, creá las cajas y empezá a registrar desde hoy. Después podés cargar comprobantes viejos y pasarlos a históricos para que no formen parte del stock ni de la caja operativa.
                    </Typography>
                    {historicalMovementCount > 0 ? (
                      <Typography sx={{ mt: .18, color: BRAND.purple, fontSize: ".6rem", fontWeight: 900 }}>
                        {historicalMovementCount} movimiento{historicalMovementCount === 1 ? "" : "s"} ya marcado{historicalMovementCount === 1 ? "" : "s"} como histórico{historicalMovementCount === 1 ? "" : "s"}.
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </Stack>
            ) : (
              <ButtonBase onClick={focusAction.onClick} sx={{ width: "100%", display: "block", textAlign: "left", borderRadius: 2.2 }}>
                <Box sx={{ p: 1, borderRadius: 2.2, bgcolor: "rgba(243,90,58,.045)", display: "grid", gridTemplateColumns: "auto minmax(0,1fr) auto", gap: .9, alignItems: "center" }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "#fff", color: BRAND.orange }}>{focusAction.icon}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: BRAND.dark, fontSize: ".84rem", fontWeight: 1000 }}>{focusAction.title}</Typography>
                    <Typography noWrap sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontSize: ".66rem" }}>{focusAction.description}</Typography>
                  </Box>
                  <ArrowForwardRoundedIcon sx={{ color: BRAND.orange, fontSize: 18 }} />
                </Box>
              </ButtonBase>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              minHeight: { xs: 0, md: 244 },
              p: { xs: 1.05, sm: 1.25 },
              borderRadius: 2.5,
              border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
              bgcolor: "var(--wp-panel-surface, #fff)",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: .6 }}>
              <Box>
                <Typography sx={{ color: BRAND.dark, fontSize: ".9rem", fontWeight: 1000 }}>Estado actual</Typography>
                <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.58)", fontSize: ".65rem" }}>Lo esencial, sin entrar a cada módulo.</Typography>
              </Box>
              <Button size="small" onClick={() => go(`${projectBase}/configuracion/general`)} sx={{ minWidth: 0, px: .65, textTransform: "none", fontSize: ".65rem", fontWeight: 900 }}>
                Configurar
              </Button>
            </Stack>

            <Stack divider={<Box sx={{ height: "1px", bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.055)" }} />}>
              {statusRows.map((status) => (
                <ButtonBase
                  key={status.key}
                  onClick={status.onClick}
                  disabled={!status.onClick}
                  sx={{ width: "100%", minHeight: 42, py: .45, textAlign: "left", borderRadius: 1.4, "&:hover": { bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)" } }}
                >
                  <Stack direction="row" alignItems="center" spacing={.8} sx={{ width: "100%" }}>
                    <Box sx={{ width: 29, height: 29, borderRadius: 1.7, display: "grid", placeItems: "center", bgcolor: status.ready ? "rgba(38,162,105,.08)" : "rgba(243,90,58,.07)", color: status.ready ? BRAND.green : BRAND.orange, flexShrink: 0 }}>
                      {status.icon}
                    </Box>
                    <Typography sx={{ minWidth: 74, color: BRAND.dark, fontSize: ".72rem", fontWeight: 900 }}>{status.label}</Typography>
                    <Typography noWrap sx={{ ml: "auto !important", minWidth: 0, color: status.ready ? BRAND.green : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", fontSize: ".68rem", fontWeight: 900 }}>
                      {status.value}
                    </Typography>
                    {status.onClick ? <ArrowForwardRoundedIcon sx={{ ml: ".35rem !important", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.32)", fontSize: 17, flexShrink: 0 }} /> : null}
                  </Stack>
                </ButtonBase>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          display: { xs: "none", sm: "block" },
          p: .9,
          borderRadius: 2.5,
          border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
          bgcolor: "var(--wp-panel-surface, #fff)",
        }}
      >
        <Grid container alignItems="center" rowSpacing={.65}>
          {[
            ["Actividad", activityMetricValue],
            ["Visitantes", visitorMetricValue],
            ["Interés", intentMetricValue],
            ["Equipo", teamMetricValue],
          ].map(([label, value]) => (
            <Grid item xs={6} sm={3} key={label}>
              <Box sx={{ px: { xs: .6, sm: 1 }, borderRight: { sm: label === "Equipo" ? "none" : "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)" } }}>
                <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.55)", fontSize: ".6rem", fontWeight: 900 }}>{label.toUpperCase()} · 7 DÍAS</Typography>
                <Typography sx={{ mt: .05, color: BRAND.dark, fontSize: { xs: ".9rem", sm: "1rem" }, lineHeight: 1.05, fontWeight: 1000 }}>{value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: { xs: "none", md: "block" }, height: "1px", bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.055)", my: .65 }} />
        <Stack direction="row" alignItems="center" spacing={.75} sx={{ display: { xs: "none", md: "flex" }, minHeight: 24, px: .4 }}>
          <HistoryRoundedIcon sx={{ color: BRAND.purple, fontSize: 17 }} />
          <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.55)", fontSize: ".63rem", fontWeight: 950, flexShrink: 0 }}>ÚLTIMA ACTIVIDAD</Typography>
          {recentActivity[0] ? (
            <>
              <Typography noWrap sx={{ minWidth: 0, color: BRAND.dark, fontSize: ".69rem", fontWeight: 900 }}>
                {activityDisplayTitle(recentActivity[0])}
              </Typography>
              <Typography noWrap sx={{ minWidth: 0, flex: 1, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.56)", fontSize: ".66rem" }}>
                {activityDisplayDescription(recentActivity[0])}
              </Typography>
              <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.5)", fontSize: ".62rem", fontWeight: 850, flexShrink: 0 }}>
                {relativeTime(recentActivity[0].occurredAt || recentActivity[0].createdAt)}
              </Typography>
            </>
          ) : (
            <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.56)", fontSize: ".66rem" }}>
              {activityLoading ? "Actualizando…" : "Todavía no hay actividad registrada."}
            </Typography>
          )}
          <Button size="small" onClick={() => go(`${projectBase}/actividad`)} sx={{ ml: "auto !important", minWidth: 0, px: .6, textTransform: "none", fontSize: ".64rem", fontWeight: 900 }}>
            Ver actividad
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
